import { useState } from 'react';
import { SpendOverTimeItem } from '../../api/types';

interface SpendTrendChartProps {
  data?: SpendOverTimeItem[];
  isLoading?: boolean;
}

const DEFAULT_SPEND_DATA: SpendOverTimeItem[] = [
  { period: '2026-01', label: 'Jan', total_amount: '3420.00', invoice_count: 14 },
  { period: '2026-02', label: 'Feb', total_amount: '4180.50', invoice_count: 18 },
  { period: '2026-03', label: 'Mar', total_amount: '6890.00', invoice_count: 24 },
  { period: '2026-04', label: 'Apr', total_amount: '5120.00', invoice_count: 19 },
  { period: '2026-05', label: 'May', total_amount: '7840.20', invoice_count: 28 },
  { period: '2026-06', label: 'Jun', total_amount: '9150.00', invoice_count: 32 },
  { period: '2026-07', label: 'Jul', total_amount: '11200.00', invoice_count: 39 },
  { period: '2026-08', label: 'Aug', total_amount: '14250.80', invoice_count: 48 },
];

export default function SpendTrendChart({
  data = DEFAULT_SPEND_DATA,
  isLoading = false,
}: SpendTrendChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const chartData = data && data.length > 0 ? data : DEFAULT_SPEND_DATA;
  const values = chartData.map((d) => parseFloat(d.total_amount) || 0);
  const maxVal = Math.max(...values, 100);

  // SVG dimensions
  const svgWidth = 600;
  const svgHeight = 220;
  const paddingX = 40;
  const paddingY = 30;

  const points = chartData.map((d, idx) => {
    const x = paddingX + (idx / (chartData.length - 1 || 1)) * (svgWidth - paddingX * 2);
    const y = svgHeight - paddingY - (parseFloat(d.total_amount) / maxVal) * (svgHeight - paddingY * 2);
    return { x, y, data: d };
  });

  const pathD = points.reduce((acc, p, idx) => {
    if (idx === 0) return `M ${p.x} ${p.y}`;
    const prev = points[idx - 1];
    const cpX1 = prev.x + (p.x - prev.x) / 2;
    const cpY1 = prev.y;
    const cpX2 = prev.x + (p.x - prev.x) / 2;
    const cpY2 = p.y;
    return `${acc} C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${p.x} ${p.y}`;
  }, '');

  const areaD = `${pathD} L ${points[points.length - 1]?.x || 0} ${svgHeight - paddingY} L ${points[0]?.x || 0} ${svgHeight - paddingY} Z`;

  return (
    <div className="col-span-12 rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-7 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-brand-vib" />
            <h3 className="text-base sm:text-lg font-bold text-brand-navy dark:text-white">
              Monthly Spend Analytics
            </h3>
          </div>
          <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
            Total invoice spend trends and volume over time
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs font-mono">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-brand-vib" />
            <span className="text-brand-navy dark:text-white font-medium">Total Spend (€)</span>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-sm text-brand-soft">
          <div className="w-6 h-6 border-2 border-brand-vib border-t-transparent rounded-full animate-spin mr-2" />
          Loading chart analytics…
        </div>
      ) : (
        <div className="relative w-full">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full h-56 sm:h-64 overflow-visible"
          >
            <defs>
              <linearGradient id="spendGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#114FEE" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#114FEE" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Horizontal Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
              const y = paddingY + ratio * (svgHeight - paddingY * 2);
              const labelVal = Math.round(maxVal * (1 - ratio));
              return (
                <g key={i}>
                  <line
                    x1={paddingX}
                    y1={y}
                    x2={svgWidth - paddingX}
                    y2={y}
                    stroke="currentColor"
                    strokeDasharray="4 4"
                    className="text-gray-100 dark:text-strokedark"
                  />
                  <text
                    x={paddingX - 8}
                    y={y + 3}
                    textAnchor="end"
                    className="text-[9px] font-mono fill-brand-soft"
                  >
                    {labelVal >= 1000 ? `${(labelVal / 1000).toFixed(0)}k` : labelVal}
                  </text>
                </g>
              );
            })}

            {/* Area Fill */}
            <path d={areaD} fill="url(#spendGradient)" />

            {/* Line Path */}
            <path
              d={pathD}
              fill="none"
              stroke="#114FEE"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Interactive Points */}
            {points.map((p, idx) => {
              const isHovered = hoveredIdx === idx;
              return (
                <g key={idx} className="cursor-pointer">
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 6 : 4}
                    className="fill-white dark:fill-boxdark stroke-[#114FEE] stroke-2 transition-all duration-150"
                  />
                  {/* Invisible hit target for smooth hover */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={18}
                    fill="transparent"
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                  />
                  {/* X Axis label */}
                  <text
                    x={p.x}
                    y={svgHeight - 10}
                    textAnchor="middle"
                    className="text-[10px] font-mono fill-brand-soft"
                  >
                    {p.data.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Hover Tooltip */}
          {hoveredIdx !== null && points[hoveredIdx] && (
            <div
              className="absolute z-20 pointer-events-none -translate-x-1/2 -translate-y-full mb-3 px-3 py-2 rounded-xl bg-brand-navy dark:bg-black text-white text-xs font-mono shadow-xl border border-white/20"
              style={{
                left: `${(points[hoveredIdx].x / svgWidth) * 100}%`,
                top: `${(points[hoveredIdx].y / svgHeight) * 100}%`,
              }}
            >
              <div className="font-bold text-brand-green">
                €{parseFloat(points[hoveredIdx].data.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] text-white/70">
                {points[hoveredIdx].data.invoice_count} invoices in {points[hoveredIdx].data.label}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
